import { z } from 'zod';
import { JabberrAvatarId } from './appearance.js';
import {
  ContextUsage,
  Conversation,
  ConversationRole,
  ConversationStatus,
  Frame,
  MemoryItem,
  MemoryKind,
  MemoryStatus,
  Output,
  StateDoc,
  StateDocKind,
  StateVersions,
  InferenceState,
  MemberRole,
  Project,
  ProjectContextVersion,
  ProjectKind,
  ProjectMember,
  ProjectStatus,
  ReasoningEffort,
  Meeting,
  MeetingSegment,
  MeetingStatus,
  Recording,
  SpeakerMap,
  Transcript,
  DocumentKind,
  IsoDate,
  NavGroup,
  ParkingLotColumn,
  ParkingLotLoopMode,
  ParkingLotLoopStatus,
  ParkingLotSize,
  PARKING_LOT_COMMENT_MAX,
  PARKING_LOT_SUMMARY_MAX,
  PARKING_LOT_TITLE_MAX,
  PARKING_LOT_WRITEUP_MAX,
  TurnRating,
  TurnWithParts,
  User,
} from './domain.js';
import { CONTEXT_HEADINGS } from './project-context.js';

// REST payloads for apps/api (prefix /api). Auth = Better Auth session cookie unless noted.

export const ErrorResponse = z.object({
  error: z.string(),
  message: z.string().optional(),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

// GET /api/me
export const MeResponse = z.object({ user: User, admin: z.boolean() });
export type MeResponse = z.infer<typeof MeResponse>;

// Admin (ADMIN_EMAILS): the company user list and account creation so a new person can sign in.
export const AdminCreateUserRequest = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120),
});
export type AdminCreateUserRequest = z.infer<typeof AdminCreateUserRequest>;

export const AdminUser = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  createdAt: z.string(),
  /** How many projects the person belongs to (their personal space excluded). */
  projects: z.number().int(),
});
export type AdminUser = z.infer<typeof AdminUser>;

export const AdminUserListResponse = z.object({ users: z.array(AdminUser) });
export type AdminUserListResponse = z.infer<typeof AdminUserListResponse>;

export const AdminUserResponse = z.object({ user: AdminUser, created: z.boolean() });
export type AdminUserResponse = z.infer<typeof AdminUserResponse>;

// GET /api/auth-config
export const MailerKind = z.enum(['console', 'acs']);
export type MailerKind = z.infer<typeof MailerKind>;

export const AuthConfigResponse = z.object({
  /** Exact origins permitted to frame the web app; status messages go only to a listed parent. */
  embedParentOrigins: z.array(z.string().url()).optional(),
  microsoft: z.boolean(),
  meetingPersonasEnabled: z.boolean().optional(),
  magicLink: z.boolean(),
  /** Returning invited guests may ask for an email sign-in link even when staff email sign-in is off. */
  guestMagicLink: z.boolean().optional(),
  /** App Store and Google Play reviewers may sign in with an email and password (REVIEW_LOGIN_EMAILS is set). */
  reviewLogin: z.boolean().optional(),
  /** GET /api/dev/magic-link is mounted (DEV_LOGIN_SHORTCUT=1 and not production). */
  devShortcut: z.boolean(),
  /** 'console' logs the link and sends no email (development); 'acs' emails it (production). */
  mailer: MailerKind,
});
export type AuthConfigResponse = z.infer<typeof AuthConfigResponse>;

// GET /api/dev/magic-link?email= (DEV_LOGIN_SHORTCUT=1 only)
export const DevMagicLinkQuery = z.object({ email: z.string().email() });
export const DevMagicLinkResponse = z.object({ url: z.string().url() });

// GET /api/health
export const HealthResponse = z.object({
  ok: z.boolean(),
  db: z.enum(['ok', 'error']),
  inference: z.object({
    state: InferenceState,
    latencyMs: z.number().nonnegative().optional(),
    model: z.string(),
    deploymentId: z.string(),
    checkedAt: z.string(),
  }),
  version: z.string(),
  transcription: z.object({
    state: z.enum(['available', 'degraded', 'unavailable']),
    readyWorkers: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    claimed: z.number().int().nonnegative(),
    expiredClaims: z.number().int().nonnegative(),
    oldestQueuedAgeS: z.number().int().nonnegative(),
    checkedAt: z.string(),
  }).optional(),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

// --- Projects ---
export const ProjectListResponse = z.object({ projects: z.array(Project) });
export type ProjectListResponse = z.infer<typeof ProjectListResponse>;

export const CreateProjectRequest = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(20_000).optional(),
  /** true: only the creator can see it until they share it (kind 'private'). */
  private: z.boolean().optional(),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequest>;

/**
 * Anchor Yard creating a team project for a codebase it follows (POST /api/internal/projects, INGEST_TOKEN).
 * Owners are existing Jabberr users named by email; the call is idempotent on the project's name.
 */
export const IngestCreateProjectRequest = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2_000).optional(),
  ownerEmails: z.array(z.string().trim().toLowerCase().email()).min(1).max(10),
});
export type IngestCreateProjectRequest = z.infer<typeof IngestCreateProjectRequest>;

export const ProjectResponse = z.object({
  project: Project,
  role: MemberRole.optional(),
  /** GET /api/projects/:id: the Anchor Yard link action is configured on this Jabberr. */
  canLinkAnchor: z.boolean().optional(),
  /** GET /api/projects/:id: an Owner may email guest invitations for this project (feature on, project filed in Yard). */
  canInviteGuests: z.boolean().optional(),
});
export type ProjectResponse = z.infer<typeof ProjectResponse>;

export const UpdateProjectRequest = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(20_000).nullable().optional(),
    status: ProjectStatus.optional(),
    /** Only 'team' is accepted, and only on a private project: the owner shares it. One way. */
    kind: z.literal('team').optional(),
    /** Where the project shows in the left nav; null returns it to the default for its kind (owner only). */
    navGroup: NavGroup.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty patch' });
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequest>;

// --- Members ---
export const MemberListResponse = z.object({ members: z.array(ProjectMember) });
export type MemberListResponse = z.infer<typeof MemberListResponse>;

export const AddMemberRequest = z.object({
  email: z.string().email(),
  role: MemberRole.default('member'),
});
export type AddMemberRequest = z.infer<typeof AddMemberRequest>;

export const MemberResponse = z.object({ member: ProjectMember });
export type MemberResponse = z.infer<typeof MemberResponse>;

// --- Project context ---
export const ContextVersionSummary = ProjectContextVersion.omit({ content: true });
export type ContextVersionSummary = z.infer<typeof ContextVersionSummary>;

export const ContextResponse = z.object({
  latest: ProjectContextVersion,
  versions: z.array(ContextVersionSummary),
});
export type ContextResponse = z.infer<typeof ContextResponse>;

export const ContextVersionResponse = z.object({ version: ProjectContextVersion });
export type ContextVersionResponse = z.infer<typeof ContextVersionResponse>;

export const PublishContextRequest = z.object({
  content: z.string().max(200_000),
  note: z.string().max(500).optional(),
  /** Which layer to publish to. Defaults to 'internal' for backward compatibility. */
  audience: z.enum(['internal', 'client']).optional(),
});
export type PublishContextRequest = z.infer<typeof PublishContextRequest>;

// POST /api/projects/:projectId/context/pins: inserts "- text" under `heading` in the latest context and publishes it
// as the next version. `text` is the user's selection (the web falls back to the turn's first sentence).
export const PinContextRequest = z.object({
  turnId: z.uuid(),
  heading: z.enum(CONTEXT_HEADINGS),
  text: z.string().trim().min(1).max(500),
});
export type PinContextRequest = z.infer<typeof PinContextRequest>;

export const PinContextResponse = z.object({
  version: ProjectContextVersion,
  /** The bullet line as inserted. */
  bullet: z.string(),
});
export type PinContextResponse = z.infer<typeof PinContextResponse>;

// --- Conversations ---
export const ConversationListResponse = z.object({
  conversations: z.array(Conversation.extend({ lastMentionAt: IsoDate.nullable(), unreadMention: z.boolean() })),
});
export type ConversationListResponse = z.infer<typeof ConversationListResponse>;

// GET /api/me/mentions: every turn that mentions the caller, created after the caller's last
// PUT /api/conversations/:conversationId/seen for that thread, newest first, at most 50.
export const MentionItem = z.object({
  conversationId: z.uuid(),
  projectId: z.uuid(),
  projectName: z.string(),
  conversationTitle: z.string().nullable(),
  mentionedAt: IsoDate,
  byName: z.string().nullable(),
  excerpt: z.string(),
});
export type MentionItem = z.infer<typeof MentionItem>;
export const MentionsResponse = z.object({ mentions: z.array(MentionItem), count: z.number().int().nonnegative() });
export type MentionsResponse = z.infer<typeof MentionsResponse>;

// GET /api/me/recent-conversations: the caller's latest threads across every project they can see (the sidebar's
// Recent), newest activity first. Threads they started or took part in; meetings never appear; guest rules hold.
export const RecentConversationItem = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectName: z.string(),
  projectKind: ProjectKind,
  title: z.string().nullable(),
  updatedAt: IsoDate,
});
export type RecentConversationItem = z.infer<typeof RecentConversationItem>;
export const RecentConversationsResponse = z.object({ conversations: z.array(RecentConversationItem) });
export type RecentConversationsResponse = z.infer<typeof RecentConversationsResponse>;

export const CreateConversationRequest = z.object({
  title: z.string().trim().max(200).optional(),
  reasoningEffort: ReasoningEffort.default('low'),
  /** Omitted: the project's default (open in My space, product owner elsewhere). */
  role: ConversationRole.optional(),
  /** Always enabled. Explicit true requires a configured service (409 search_unavailable); omission degrades quietly. */
  webSearch: z.boolean().default(true),
  /** Group threads (OPEN-23): 'mentions' answers only @jabber turns (default), 'always' answers every turn,
   *  'keen' asks the decision sidecar whether Jabberr has something to add (silence on doubt or on error). */
  listenMode: z.enum(['always', 'mentions', 'keen', 'silent']).default('mentions'),
});
/** The input shape: defaults may be omitted by the client. */
export type CreateConversationRequest = z.input<typeof CreateConversationRequest>;

export const ConversationResponse = z.object({ conversation: Conversation });
export type ConversationResponse = z.infer<typeof ConversationResponse>;

export const ConversationDetailResponse = z.object({
  conversation: Conversation,
  turns: z.array(TurnWithParts),
  /** Fullness of the next prompt, so the web can warn at 70 percent. */
  context: ContextUsage,
});
export type ConversationDetailResponse = z.infer<typeof ConversationDetailResponse>;

export const UpdateConversationRequest = z
  .object({
    role: ConversationRole.optional(),
    expectedRole: ConversationRole.optional(),
    title: z.string().trim().max(200).optional(),
    status: ConversationStatus.optional(),
    listenMode: z.enum(['always', 'mentions', 'keen', 'silent']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty patch' })
  .refine((v) => v.role === undefined ? v.expectedRole === undefined : v.title === undefined && v.status === undefined && v.listenMode === undefined, {message: 'role changes must be a separate patch'});
export type UpdateConversationRequest = z.infer<typeof UpdateConversationRequest>;

// POST /api/conversations/:id/messages: appends the user turn and answers 201 at once (no stream on the POST).
// Live events, including the assistant reply, flow through GET /api/conversations/:id/events (see sse.ts).
// 409 on duplicate clientTurnId or when a reply is wanted while another generation runs.
export const SendMessageRequest = z
  .object({
    clientTurnId: z.string().min(1).max(128),
    content: z.string().min(1).max(100_000).optional(),
    /** A document kind: the server sends and stores its own copy of that prompt, never `content`. */
    purpose: DocumentKind.optional(),
    /** Source text quoted into the document turn after the fixed prompt (the transcript segment a
     *  requirements draft was pulled from). Only meaningful together with a purpose. */
    source: z.string().min(1).max(20_000).optional(),
  })
  .refine((v) => v.purpose !== undefined || v.content !== undefined, { message: 'content is required without a purpose' })
  .refine((v) => v.source === undefined || v.purpose !== undefined, { message: 'source requires a purpose' });
export type SendMessageRequest = z.infer<typeof SendMessageRequest>;

export const SendMessageResponse = z.object({
  userTurnId: z.uuid(),
  seq: z.number().int().positive(),
  /** Present when the reply was triggered: the assistant turn the events channel will stream into. */
  assistantTurnId: z.uuid().nullable(),
  replied: z.boolean(),
});
export type SendMessageResponse = z.infer<typeof SendMessageResponse>;

// POST /api/conversations/:id/turns/:userTurnId/regenerate -> 201 with the fresh assistant turn; the same
// events channel streams it. 409 unless it is the latest user turn and nothing is streaming.
export const RegenerateResponse = z.object({ assistantTurnId: z.uuid() });
export type RegenerateResponse = z.infer<typeof RegenerateResponse>;

export const SendMessageConflictResponse = z.object({
  error: z.literal('duplicate_client_turn_id'),
  existingTurnId: z.uuid(),
});
export type SendMessageConflictResponse = z.infer<typeof SendMessageConflictResponse>;

// POST /api/conversations/:id/turns/:turnId/cancel
export const CancelTurnResponse = z.object({
  ok: z.literal(true),
  cancelled: z.boolean(), // false when nothing was in flight
});
export type CancelTurnResponse = z.infer<typeof CancelTurnResponse>;

// --- Recordings ---
export const CreateRecordingRequest = z.object({
  title: z.string().trim().min(1).max(200),
  source: z.enum(['upload', 'browser']),
  mime: z.string().min(1),
});
export type CreateRecordingRequest = z.infer<typeof CreateRecordingRequest>;

export const CreateRecordingResponse = z.object({
  recordingId: z.uuid(),
  uploadUrl: z.string().optional(),
});
export type CreateRecordingResponse = z.infer<typeof CreateRecordingResponse>;

// PUT /api/recordings/:id/chunks/:seq (raw application/octet-stream body)
export const ChunkUploadResponse = z.object({
  ok: z.literal(true),
  seq: z.number().int().nonnegative(),
  sizeBytes: z.number().int().nonnegative(),
});
export type ChunkUploadResponse = z.infer<typeof ChunkUploadResponse>;

export const CompleteRecordingRequest = z.object({
  chunkCount: z.number().int().positive(),
});
export type CompleteRecordingRequest = z.infer<typeof CompleteRecordingRequest>;

export const ImportVttRequest = z.object({
  title: z.string().trim().min(1).max(200),
  vtt: z.string().min(1).max(5_000_000),
});
export type ImportVttRequest = z.infer<typeof ImportVttRequest>;

export const RecordingListResponse = z.object({ recordings: z.array(Recording) });
export type RecordingListResponse = z.infer<typeof RecordingListResponse>;

export const RecordingDetailResponse = z.object({
  /** The assistant artwork of the meeting this recording belongs to (businessman shows as professional here). */
  jabberrAvatarId: JabberrAvatarId.optional(),
  /** The real choice, any catalog id. */
  jabberrAssistantImageId: JabberrAvatarId.optional(),
  recording: Recording,
  transcript: Transcript.optional(),
});
export type RecordingDetailResponse = z.infer<typeof RecordingDetailResponse>;

export const UpdateSpeakersRequest = z.object({ speakerMap: SpeakerMap });
export type UpdateSpeakersRequest = z.infer<typeof UpdateSpeakersRequest>;

export const TranscriptResponse = z.object({ transcript: Transcript });
export type TranscriptResponse = z.infer<typeof TranscriptResponse>;

// POST /api/recordings/:id/review -> the web app immediately POSTs /messages with firstTurn.
// 409 transcript_too_long when the prefix plus the first turn would not fit the prompt budget.
export const TranscriptTooLongResponse = z.object({
  error: z.literal('transcript_too_long'),
  message: z.string().optional(),
  tokens: z.number().int().nonnegative(),
  budget: z.number().int().nonnegative(),
});
export type TranscriptTooLongResponse = z.infer<typeof TranscriptTooLongResponse>;

export const ReviewRecordingResponse = z.object({
  conversationId: z.uuid(),
  firstTurn: z.object({
    clientTurnId: z.string(),
    content: z.string(),
  }),
});
export type ReviewRecordingResponse = z.infer<typeof ReviewRecordingResponse>;

// --- State documents (docs/adr/OPEN-16) ---
// GET /api/projects/:projectId/state -> the latest version of each kind that has one
export const StateDocsResponse = z.object({ docs: z.array(StateDoc) });
export type StateDocsResponse = z.infer<typeof StateDocsResponse>;

// PUT /api/projects/:projectId/state/:kind (member, by hand)
// PUT /api/internal/projects/:slug/state/:kind (Authorization: Bearer <INGEST_TOKEN>; CI and the nightly job)
export const PutStateDocRequest = z.object({
  content: z.string(),
  source: z.string().trim().min(1).max(40).optional(),
  sourceRef: z.string().trim().max(200).optional(),
});
export type PutStateDocRequest = z.infer<typeof PutStateDocRequest>;

/** changed false: the content hash matched the latest version and nothing was written. */
export const PutStateDocResponse = z.object({ doc: StateDoc, changed: z.boolean() });
export type PutStateDocResponse = z.infer<typeof PutStateDocResponse>;

// --- Memory (docs/adr/OPEN-17) ---
// GET /api/projects/:projectId/memory -> project items (every status) plus the caller's own user items
export const MemoryListResponse = z.object({ project: z.array(MemoryItem), user: z.array(MemoryItem) });
export type MemoryListResponse = z.infer<typeof MemoryListResponse>;

// POST /api/projects/:projectId/memory (kind 'preference' creates a user item for the caller)
export const CreateMemoryRequest = z.object({
  kind: MemoryKind,
  text: z.string().trim().min(1).max(500),
  frame: Frame.default('general'),
});
export type CreateMemoryRequest = z.infer<typeof CreateMemoryRequest>;

// PATCH /api/memory/:memoryId
export const UpdateMemoryRequest = z
  .object({
    text: z.string().trim().min(1).max(500).optional(),
    kind: MemoryKind.optional(),
    frame: Frame.optional(),
    status: MemoryStatus.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty patch' });
export type UpdateMemoryRequest = z.infer<typeof UpdateMemoryRequest>;

export const MemoryItemResponse = z.object({ item: MemoryItem });
export type MemoryItemResponse = z.infer<typeof MemoryItemResponse>;

// POST /api/conversations/:conversationId/memory/extract -> runs extraction now over the turns not yet covered
export const ExtractMemoryResponse = z.object({
  /** false: nothing new to read, or the model call did not complete. */
  ran: z.boolean(),
  added: z.array(MemoryItem),
  /** Some earlier batches saved; retry continues from their persisted cursor. */
  pending: z.boolean().optional(),
});
export type ExtractMemoryResponse = z.infer<typeof ExtractMemoryResponse>;

// --- Outputs (requirements drafts) ---
// GET /api/prompts/documents -> every document kind with the fixed user turn the web sends for it
export const DocumentPrompt = z.object({ kind: DocumentKind, label: z.string(), description: z.string(), version: z.number().int().nonnegative(), content: z.string() });
export type DocumentPrompt = z.infer<typeof DocumentPrompt>;
export const DocumentPromptsResponse = z.object({ documents: z.array(DocumentPrompt) });
export type DocumentPromptsResponse = z.infer<typeof DocumentPromptsResponse>;

// POST /api/projects/:id/write: draft a document across threads. The server assembles the fixed
// document turn plus excerpts of the selected threads into a seeded first turn, creates the draft
// conversation, and the web sends that turn through the normal pipeline (like transcript review).
export const WriteRequest = z.object({
  purpose: DocumentKind,
  /** Source threads, in the order they should be read. */
  conversationIds: z.array(z.uuid()).min(1).max(8),
  notes: z.string().max(4_000).optional(),
  /** The draft's role, defaulting to the project's default (OPEN-37). Must be an internal-audience role;
   *  the server rejects client_partner (drafting is internal work, the same rule that already forbids
   *  a guest from this route). */
  role: ConversationRole.optional(),
});
export type WriteRequest = z.infer<typeof WriteRequest>;

export const WriteResponse = z.object({
  conversationId: z.uuid(),
  firstTurn: z.object({ clientTurnId: z.string(), content: z.string(), purpose: DocumentKind }),
});
export type WriteResponse = z.infer<typeof WriteResponse>;

// GET /api/feedback/config: whether the shared visual feedback overlay is on for this Jabberr, and
// where its widget script lives. The overlay itself talks to /feedback/... on this origin.
export const FeedbackConfigResponse = z.object({
  enabled: z.boolean(),
  /** Widget script origin, present only when enabled. */
  base: z.string().optional(),
  /** The feedback project id this Jabberr reports as, present only when enabled. */
  project: z.string().optional(),
});
export type FeedbackConfigResponse = z.infer<typeof FeedbackConfigResponse>;

// PATCH /api/conversations/:conversationId/turns/:turnId -> keep any completed reply on the Outputs page, or drop it again
export const UpdateTurnRequest = z.object({ saved: z.boolean() });
export type UpdateTurnRequest = z.infer<typeof UpdateTurnRequest>;
export const TurnResponse = z.object({ turn: TurnWithParts });
export type TurnResponse = z.infer<typeof TurnResponse>;

// GET /api/projects/:projectId/outputs -> newest first
export const OutputListResponse = z.object({ outputs: z.array(Output) });
export type OutputListResponse = z.infer<typeof OutputListResponse>;

// PUT /api/conversations/:conversationId/turns/:turnId/rating -> thumbs on a completed reply (null clears it)
export const UpdateTurnRatingRequest = z.object({ rating: TurnRating.nullable() });
export type UpdateTurnRatingRequest = z.infer<typeof UpdateTurnRatingRequest>;

// GET /api/projects/:projectId/rated-replies?rating=down -> rated replies newest first, each with what the person asked,
// the role and prompt version that answered, the model request behind it, and a smoke-set case draft (docs/smoke).
export const RatedRepliesQuery = z.object({ rating: TurnRating.default('down') });
export type RatedRepliesQuery = z.infer<typeof RatedRepliesQuery>;
export const SmokeCaseDraft = z.object({
  id: z.string(),
  title: z.string(),
  modality: z.enum(['text', 'voice']),
  context: z.string().nullable(),
  turns: z.array(z.string()),
  scoreTurn: z.number().int(),
  must: z.array(z.string()),
  failsIf: z.array(z.string()),
});
export type SmokeCaseDraft = z.infer<typeof SmokeCaseDraft>;
export const RatedReply = z.object({
  turnId: z.uuid(),
  conversationId: z.uuid(),
  conversationTitle: z.string().nullable(),
  rating: TurnRating,
  role: ConversationRole,
  rolePromptVersion: z.number().int().nullable(),
  contextVersion: z.number().int(),
  modality: z.enum(['text', 'voice']),
  prompt: z.string(),
  reply: z.string(),
  requestId: z.string().nullable(),
  createdAt: z.string(),
  smokeCase: SmokeCaseDraft,
});
export type RatedReply = z.infer<typeof RatedReply>;
export const RatedRepliesResponse = z.object({ replies: z.array(RatedReply) });
export type RatedRepliesResponse = z.infer<typeof RatedRepliesResponse>;

// --- Search ---
// GET /api/search?q= -> membership-aware: projects, conversations, turn text, memory and state documents the caller can see
export const SearchQuery = z.object({ q: z.string().trim().min(2).max(200) });
export const SearchResultItem = z.object({
  kind: z.enum(['project', 'conversation', 'turn', 'memory', 'state']),
  projectId: z.uuid(),
  projectName: z.string(),
  conversationId: z.uuid().optional(),
  conversationTitle: z.string().nullable().optional(),
  turnId: z.uuid().optional(),
  snippet: z.string(),
  updatedAt: IsoDate,
});
export type SearchResultItem = z.infer<typeof SearchResultItem>;
export const SearchResponse = z.object({ results: z.array(SearchResultItem) });
export type SearchResponse = z.infer<typeof SearchResponse>;

// GET /api/projects/:projectId/outputs/:turnId
export const OutputResponse = z.object({
  output: Output,
  markdown: z.string(),
  /** Save to the Workbench is on (Workbench and Yard configured) and this output's kind can be saved. */
  canSaveToWorkbench: z.boolean().optional(),
});
export type OutputResponse = z.infer<typeof OutputResponse>;

// --- Route params ---
export const ProjectIdParams = z.object({ projectId: z.uuid() });
export const ConversationIdParams = z.object({ conversationId: z.uuid() });
export const TurnIdParams = z.object({ conversationId: z.uuid(), turnId: z.uuid() });
export const RecordingIdParams = z.object({ recordingId: z.uuid() });
export const TranscriptIdParams = z.object({ transcriptId: z.uuid() });
export const ContextVersionParams = z.object({
  projectId: z.uuid(),
  version: z.coerce.number().int().positive(),
});
export const ChunkParams = z.object({
  recordingId: z.uuid(),
  seq: z.coerce.number().int().nonnegative(),
});
export const JobIdParams = z.object({ jobId: z.uuid() });
export const StateDocParams = z.object({ projectId: z.uuid(), kind: StateDocKind });
export const IngestStateDocParams = z.object({ slug: z.string().min(1).max(200), kind: StateDocKind });
export const MemoryIdParams = z.object({ memoryId: z.uuid() });
export const OutputParams = z.object({ projectId: z.uuid(), turnId: z.uuid() });

// POST /api/tts: the user turn text to speak; the api proxies it to the TTS worker (workers/voice).
// 503 tts_unavailable when the worker is unset or unreachable; the web then falls back to the browser voice.
export const TtsRequest = z.object({
  text: z.string().trim().min(1).max(5000),
});
export type TtsRequest = z.infer<typeof TtsRequest>;

// --- Presence and room invites (apps/api/src/routes/presence.ts) ---
export const UserStatus = z.enum(['online', 'offline', 'unavailable']);
export type UserStatus = z.infer<typeof UserStatus>;
export const PresenceUser = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  status: UserStatus,
  /** Whether the user holds a Better Auth session whose expiry is in the future. */
  hasActiveSession: z.boolean(),
});
export type PresenceUser = z.infer<typeof PresenceUser>;
export const PresenceUsersResponse = z.object({ users: z.array(PresenceUser) });
export type PresenceUsersResponse = z.infer<typeof PresenceUsersResponse>;
export const SetStatusRequest = z.object({ status: UserStatus });
export type SetStatusRequest = z.infer<typeof SetStatusRequest>;
export const InviteUserRequest = z.object({ userId: z.uuid() });
export type InviteUserRequest = z.infer<typeof InviteUserRequest>;
export const InvitesResponse = z.object({
  invites: z.array(z.object({ meetingId: z.uuid(), title: z.string(), invitedBy: z.string() })),
});
export type InvitesResponse = z.infer<typeof InvitesResponse>;

// POST /api/asr: the user turn audio bytes; the api proxies them to the ASR worker (workers/voice).
// 503 asr_unavailable when the worker is unset or unreachable; the web then falls back to the browser recognizer.
export const AsrResponse = z.object({
  text: z.string(),
});
export type AsrResponse = z.infer<typeof AsrResponse>;

// POST /api/conversations/:id/room/jabberr: invites the Jabberr room bot (dispatched by the LiveKit
// server, OPEN-29); 202 with the conversation's listen mode, which the invite sets to 'keen' unless
// it was deliberately 'always'. 503 room_unavailable when LiveKit is not configured or unreachable.
export const RoomInviteResponse = z.object({
  listenMode: z.enum(['always', 'mentions', 'keen', 'silent']),
});
export type RoomInviteResponse = z.infer<typeof RoomInviteResponse>;

// POST /api/internal/conversations/:id/messages: the room bot's spoken turn. replied false means
// Jabberr stays silent (listen mode or the keen decider said no) and the bot speaks nothing.
export const WorkerChatResponse = z.object({
  replyText: z.string().nullable(),
  replied: z.boolean(),
});
export type WorkerChatResponse = z.infer<typeof WorkerChatResponse>;

// The same route with Accept: application/x-ndjson streams newline-delimited JSON lines: a decision
// line as soon as the listen mode decision is made and the user turn is stored, then (only when
// replied is true) a reply line after generation. Without the header the route answers one JSON
// object (WorkerChatResponse) as before, because the worker and the api deploy separately.
export const WorkerChatDecisionLine = z.object({
  type: z.literal('decision'),
  replied: z.boolean(),
  userTurnId: z.uuid(),
});
export type WorkerChatDecisionLine = z.infer<typeof WorkerChatDecisionLine>;
export const WorkerChatReplyLine = z.object({
  type: z.literal('reply'),
  replyText: z.string().nullable(),
  status: z.string(),
});
export type WorkerChatReplyLine = z.infer<typeof WorkerChatReplyLine>;

// --- Meetings (docs/adr/OPEN-31): voice rooms with their own transcripts ---

export const MeetingIdParams = z.object({ meetingId: z.uuid() });
export const MeetingTranscriptQuery = z.object({ after: z.coerce.number().int().nonnegative().default(0) });

// POST /api/projects/:projectId/meetings: starts a meeting (one transaction creates the meeting,
// a source 'meeting' recording in status 'live', an empty transcript, and the hidden conversation).
export const MeetingPersona = z.enum(['product_owner', 'sales_executive']);
export const MeetingCapabilitiesResponse = z.object({
  version: z.literal(1), projectId: z.uuid(), roles: z.array(MeetingPersona),
});
export type MeetingCapabilitiesResponse = z.infer<typeof MeetingCapabilitiesResponse>;
export const MeetingContextResponse = z.object({
  version: z.literal(1), meetingId: z.uuid(), projectId: z.uuid(), conversationId: z.uuid(),
  role: ConversationRole, audience: z.enum(['internal', 'client']),
  globalPromptVersion: z.number().int().positive(), contextVersion: z.number().int().nonnegative(),
  clientContextVersion: z.number().int().positive().nullable(), stateVersions: StateVersions,
});
export type MeetingContextResponse = z.infer<typeof MeetingContextResponse>;
export const ReviewRecordingRequest = z.object({ meetingId: z.uuid().optional() });
export const CreateMeetingRequest = z.object({
  /** The assistant artwork for this meeting; absent uses the starter's saved choice, else the default. */
  jabberrAvatarId: JabberrAvatarId.optional(),
  role: MeetingPersona.optional(),
  title: z.string().trim().min(1).max(200).optional(),
  listenMode: z.enum(['always', 'mentions', 'keen', 'silent']).default('keen'),
});
export type CreateMeetingRequest = z.input<typeof CreateMeetingRequest>;
export const CreateMeetingResponse = z.object({ meeting: Meeting });
export type CreateMeetingResponse = z.infer<typeof CreateMeetingResponse>;

export const MeetingListResponse = z.object({ meetings: z.array(Meeting.extend({ canEnd: z.boolean() })) });
export type MeetingListResponse = z.infer<typeof MeetingListResponse>;

// GET /api/meetings/:meetingId: the meeting plus its transcript so far.
export const MeetingResponse = z.object({
  meeting: Meeting,
  transcriptId: z.uuid().nullable(),
  project: z.object({ id: z.uuid(), name: z.string() }),
  /** True for the person who started the meeting and for project owners: the server decides, the page only reads it. */
  canEnd: z.boolean(),
});
export type MeetingResponse = z.infer<typeof MeetingResponse>;

// GET /api/meetings/:meetingId/transcript?after=n: segments from index n on (live following).
export const MeetingTranscriptResponse = z.object({
  meetingId: z.uuid(),
  status: MeetingStatus,
  /** Index of the first returned segment within the full transcript. */
  from: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  /** Index the next poll should ask for (from + segments.length). */
  next: z.number().int().nonnegative(),
  segments: z.array(MeetingSegment),
});
export type MeetingTranscriptResponse = z.infer<typeof MeetingTranscriptResponse>;

export const UpdateMeetingRequest = z.object({
  role: z.never().optional(),
  title: z.string().trim().min(1).max(200).optional(),
  listenMode: z.enum(['always', 'mentions', 'keen', 'silent']).optional(),
});
export type UpdateMeetingRequest = z.input<typeof UpdateMeetingRequest>;
export const UpdateMeetingResponse = z.object({ meeting: Meeting });
export type UpdateMeetingResponse = z.infer<typeof UpdateMeetingResponse>;

// POST /api/meetings/:meetingId/end: status 'ended', endedAt set, the recording becomes 'ready'.
export const EndMeetingResponse = z.object({ meeting: Meeting });
export type EndMeetingResponse = z.infer<typeof EndMeetingResponse>;

// POST /api/internal/meetings/:meetingId/utterances: the bot's spoken utterance for a meeting. The
// segment is always appended; the listen mode decides whether Jabberr answers (ndjson like worker
// chat: a decision line with segmentIndex, then a reply line when replying).
export const MeetingControlRequest = z.object({ action: z.enum(['answer', 'stop']) });
export const MeetingFeedbackRequest = z.object({ responseId: z.string().uuid(), rating: z.enum(['useful', 'not_needed', 'bad_timing']) });
export const MeetingCommandClaimRequest = z.object({ commandId: z.string().uuid() });
export const MeetingInterruptRequest = z.object({ responseId: z.string().uuid() });
export const MeetingPlaybackRequest = z.object({ responseId: z.string().uuid(), index: z.number().int().nonnegative() });

export const MeetingParticipationCommand = z.object({ id: z.string().uuid(), action: z.enum(['answer', 'stop']), actorName: z.string() });
export const MeetingParticipationResponse = z.object({ revision: z.string().uuid().optional(), command: MeetingParticipationCommand.nullable(), canAnswerSharedInput: z.boolean().optional() });
export type MeetingParticipationResponse = z.infer<typeof MeetingParticipationResponse>;

export const MeetingUtteranceBody = z.object({
  text: z.string().trim().min(1).max(20_000),
  speakerUserId: z.string().min(1),
  recordOnly: z.boolean().optional(),
  /** Stable capture identity; retries reuse it, fresh speech gets a new one. */
  utteranceId: z.string().uuid().optional(),
  utteranceRevision: z.number().int().nonnegative().optional(),
  segmentIndex: z.number().int().nonnegative().optional(),
  responseId: z.string().uuid().optional(),
  revision: z.string().uuid().optional(),
  expected: z.boolean().optional(),
});
export type MeetingUtteranceBody = z.input<typeof MeetingUtteranceBody>;
export const MeetingUtteranceResponse = z.object({
  replyText: z.string().nullable(),
  replied: z.boolean(),
  /** Record-only ingestion preserves an explicit invitation across queued clarifications. */
  addressed: z.boolean().optional(),
  /** A referential follow-up to an existing accepted reply retains answer permission. */
  followup: z.boolean().optional(),
  interrupts: z.boolean().optional(),
  revision: z.string().uuid().optional(),
  /** Index of the appended segment in the meeting transcript. */
  segmentIndex: z.number().int().nonnegative(),
});
export type MeetingUtteranceResponse = z.infer<typeof MeetingUtteranceResponse>;
export const MeetingUtteranceDecisionLine = z.object({
  type: z.literal('decision'),
  replied: z.boolean(),
  segmentIndex: z.number().int().nonnegative(),
});
export type MeetingUtteranceDecisionLine = z.infer<typeof MeetingUtteranceDecisionLine>;
// Sentence streaming (docs/adr/OPEN-31): between the decision line and the reply line, one speech
// line per finished piece of speech as the content streams. The reply line keeps the worker chat
// shape and adds spoken true when at least one speech line was written, so an old worker that
// ignores speech lines and unknown fields keeps working.
export const MeetingUtteranceSpeechLine = z.object({
  type: z.literal('speech'),
  index: z.number().int().nonnegative(),
  text: z.string().min(1),
});
export type MeetingUtteranceSpeechLine = z.infer<typeof MeetingUtteranceSpeechLine>;
export const MeetingUtteranceReplyLine = WorkerChatReplyLine.extend({
  spoken: z.boolean().optional(),
});
export type MeetingUtteranceReplyLine = z.infer<typeof MeetingUtteranceReplyLine>;

// Content-bearing feedback is stored in the raw corpus, never in telemetry.
const FeedbackMoment = { expectedActorId: z.string().min(1).max(200).optional(), submissionId: z.string().uuid(), observedAt: z.string().datetime(), segmentIndex: z.number().int().nonnegative().optional() };
export const CallCorpusRequest = z.discriminatedUnion('kind', [
  z.object({ ...FeedbackMoment, kind: z.literal('call'), stars: z.number().int().min(1).max(5).optional(), comment: z.string().trim().max(2000).default(''), categories: z.array(z.enum(['sound', 'helpfulness', 'missed_intervention', 'incorrect_intervention'])).max(4).default([]) }).strict().refine(x => x.stars !== undefined || x.comment.length > 0 || x.categories.length > 0, 'Provide a rating or feedback'),
  z.object({ ...FeedbackMoment, kind: z.literal('missed_intervention'), segmentIndex: z.number().int().nonnegative(), comment: z.string().trim().max(2000).default('') }).strict(),
  z.object({ ...FeedbackMoment, kind: z.literal('contribution'), responseId: z.string().uuid(), rating: z.enum(['useful', 'not_needed', 'bad_timing']) }).strict(),
]);
export type CallCorpusRequest = z.infer<typeof CallCorpusRequest>;
export const CorpusReceipt = z.object({ corpusId: z.string().uuid(), state: z.enum(['raw', 'accepted', 'excluded']), duplicate: z.boolean() });
export const CorpusReviewRequest = z.object({ state: z.enum(['accepted', 'excluded']), shouldInterject: z.boolean().optional(), expectedBehavior: z.string().trim().max(2000).default('') }).strict();
export const CorpusExportQuery = z.object({ projectId: z.string().uuid(), offset: z.coerce.number().int().min(0).max(10000000).default(0) });

// --- Parking lot (anchor-loop decisions 2026-10-09 #6) ---
// Audience is structural (OPEN-27): a person on the team sees every idea; anyone else sees their own ideas and the
// client-audience ideas of projects they are a guest in, and nothing else. Routes answer 404 for an idea the caller
// cannot see and 403 for one they can see but not change.
/** Where an idea's requirements stand in Loop (Parking lot Part 2). */
export const ParkingLotLoop = z.object({
  status: ParkingLotLoopStatus,
  mode: ParkingLotLoopMode,
  /** Yard's Owner request id. */
  requestId: z.string(),
  sentAt: IsoDate,
  sentByName: z.string().nullable(),
  /** Extend: the Jabberr project the requirements go to. */
  targetProjectId: z.uuid().nullable(),
  targetProjectName: z.string().nullable(),
  decidedAt: IsoDate.nullable(),
  decidedByName: z.string().nullable(),
  /** Sent back: the note John or Adam wrote. Failed: what Yard said. */
  reason: z.string().nullable(),
  /** Approved: the Yard project and plan. */
  yardProjectId: z.string().nullable(),
  yardProjectName: z.string().nullable(),
  packetId: z.string().nullable(),
});
export type ParkingLotLoop = z.infer<typeof ParkingLotLoop>;

export const ParkingLotItem = z.object({
  id: z.uuid(),
  /** Null: an idea for a new project (from My space, or added with no project). */
  projectId: z.uuid().nullable(),
  projectName: z.string().nullable(),
  projectKind: z.string().nullable(),
  /** The idea's project is a private idea project (OPEN-38): it is named after its first note. */
  ideaProject: z.boolean(),
  /** Continue the conversation: an idea project the caller may open (its owner, or someone invited into it). */
  canContinue: z.boolean(),
  audience: z.enum(['internal', 'client']),
  title: z.string(),
  summary: z.string(),
  size: ParkingLotSize,
  column: ParkingLotColumn,
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  /** The conversation the idea came from; opening it still follows the conversation's own rules. */
  sourceConversationId: z.uuid().nullable(),
  /** The project that conversation lives in (for its link). */
  sourceProjectId: z.uuid().nullable(),
  /** Build this: the conversation the requirements draft went to and the draft reply (an Outputs turn). */
  buildConversationId: z.uuid().nullable(),
  buildTurnId: z.uuid().nullable(),
  buildProjectId: z.uuid().nullable(),
  commentCount: z.number().int().nonnegative(),
  /** The caller may change it: anyone on the team, or the person who added it. */
  canEdit: z.boolean(),
  /** Its requirements in Loop, once sent (Parking lot Part 2). */
  loop: ParkingLotLoop.nullable(),
  /** The caller may send its requirements to Loop now: drafted, not waiting or approved, and the caller is the idea's
   *  Owner (or an Owner of a project it could extend). The server checks again, with Yard's submit-to-loop grant. */
  canSendToLoop: z.boolean(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type ParkingLotItem = z.infer<typeof ParkingLotItem>;

export const ParkingLotItemDetail = ParkingLotItem.extend({
  writeup: z.string(),
  sourceTurnIds: z.array(z.uuid()),
});
export type ParkingLotItemDetail = z.infer<typeof ParkingLotItemDetail>;

/** The parts of a note a suggestion can change: its title, its one line, or a write-up section by heading. */
export const NOTE_PARTS = ['Title', 'Summary', 'What it is', "Who it's for and why", 'Rough shape', 'Still open'] as const;
export const NoteSuggestion = z.object({ part: z.string().min(1).max(80), text: z.string().max(PARKING_LOT_WRITEUP_MAX) });
export type NoteSuggestion = z.infer<typeof NoteSuggestion>;

export const ParkingLotComment = z.object({
  id: z.uuid(),
  itemId: z.uuid(),
  authorId: z.string().nullable(),
  authorName: z.string().nullable(),
  byJabberr: z.boolean(),
  /** 'edit': a person changed the note with Use this; replyToId names the suggestion they used. 'loop': what happened
   *  in Loop (sent, approved, sent back with its note), written by Jabberr's server, never by the model. */
  kind: z.enum(['comment', 'edit', 'loop']),
  replyToId: z.uuid().nullable(),
  body: z.string(),
  /** On Jabberr's answers: a change to the note it suggests, applied only when a person presses Use this. */
  suggestion: NoteSuggestion.nullable(),
  /** The suggestion has been used (by the edit that names it). */
  applied: z.boolean(),
  createdAt: IsoDate,
});
export type ParkingLotComment = z.infer<typeof ParkingLotComment>;

/** ?project=<uuid> | new (ideas with no project) ; ?column=<column> */
export const ParkingLotListQuery = z.object({
  project: z.union([z.uuid(), z.literal('new')]).optional(),
  column: ParkingLotColumn.optional(),
});
export type ParkingLotListQuery = z.infer<typeof ParkingLotListQuery>;

export const ParkingLotListResponse = z.object({
  items: z.array(ParkingLotItem),
  /** 'team': the Tarawa view (everything); 'own': the caller's own ideas plus their client projects' ideas. */
  scope: z.enum(['team', 'own']),
});
export type ParkingLotListResponse = z.infer<typeof ParkingLotListResponse>;

export const ParkingLotCountResponse = z.object({ open: z.number().int().nonnegative() });
export type ParkingLotCountResponse = z.infer<typeof ParkingLotCountResponse>;

/** Where the caller may send the requirements: a new project (a new-project idea's Owner), or one of these projects. */
export const ParkingLotLoopOptions = z.object({
  newProject: z.boolean(),
  projects: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type ParkingLotLoopOptions = z.infer<typeof ParkingLotLoopOptions>;

export const ParkingLotDetailResponse = z.object({ item: ParkingLotItemDetail, comments: z.array(ParkingLotComment), loopOptions: ParkingLotLoopOptions.nullable().optional() });
export type ParkingLotDetailResponse = z.infer<typeof ParkingLotDetailResponse>;

export const CreateParkingLotItemRequest = z.object({
  title: z.string().trim().min(1).max(PARKING_LOT_TITLE_MAX),
  summary: z.string().trim().max(PARKING_LOT_SUMMARY_MAX).default(''),
  writeup: z.string().max(PARKING_LOT_WRITEUP_MAX).default(''),
  size: ParkingLotSize.default('unknown'),
  /** Omitted or null: a new project idea. */
  projectId: z.uuid().nullable().optional(),
});
export type CreateParkingLotItemRequest = z.infer<typeof CreateParkingLotItemRequest>;

/** POST /api/conversations/:conversationId/parking-lot: Jabberr drafts the note from the conversation. */
export const ParkFromConversationRequest = z.object({ clientTurnId: z.string().min(1).max(100) });
export type ParkFromConversationRequest = z.infer<typeof ParkFromConversationRequest>;

export const UpdateParkingLotItemRequest = z
  .object({
    title: z.string().trim().min(1).max(PARKING_LOT_TITLE_MAX).optional(),
    summary: z.string().trim().max(PARKING_LOT_SUMMARY_MAX).optional(),
    writeup: z.string().max(PARKING_LOT_WRITEUP_MAX).optional(),
    size: ParkingLotSize.optional(),
    column: ParkingLotColumn.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty patch' });
export type UpdateParkingLotItemRequest = z.infer<typeof UpdateParkingLotItemRequest>;

export const ParkingLotItemResponse = z.object({ item: ParkingLotItemDetail });
export type ParkingLotItemResponse = z.infer<typeof ParkingLotItemResponse>;

export const ParkFromConversationResponse = z.object({ item: ParkingLotItemDetail, assistantTurnId: z.uuid() });
export type ParkFromConversationResponse = z.infer<typeof ParkFromConversationResponse>;

export const PostParkingLotCommentRequest = z.object({ body: z.string().trim().min(1).max(PARKING_LOT_COMMENT_MAX) });
export type PostParkingLotCommentRequest = z.infer<typeof PostParkingLotCommentRequest>;

export const PostParkingLotCommentResponse = z.object({
  comment: ParkingLotComment,
  /** Jabberr's answer when the comment named @Jabberr and the model answered; null otherwise. */
  reply: ParkingLotComment.nullable(),
  /** Why there is no reply to an @Jabberr comment: the model was unavailable, or the idea has no conversation yet. */
  jabberr: z.enum(['replied', 'not_asked', 'unavailable', 'no_conversation']),
});
export type PostParkingLotCommentResponse = z.infer<typeof PostParkingLotCommentResponse>;

/** POST /api/parking-lot/:itemId/send-to-loop: the drafted requirements go to Loop for John's or Adam's approval, as a
 *  new project or to extend one of the caller's projects. */
export const SendToLoopRequest = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('new') }),
  z.object({ mode: z.literal('extend'), projectId: z.uuid() }),
]);
export type SendToLoopRequest = z.infer<typeof SendToLoopRequest>;

/** POST /api/parking-lot/:itemId/build: the Draft requirements turn, sent in a conversation linked to the idea. */
export const BuildParkingLotItemResponse = z.object({
  conversationId: z.uuid(),
  projectId: z.uuid(),
  assistantTurnId: z.uuid(),
});
export type BuildParkingLotItemResponse = z.infer<typeof BuildParkingLotItemResponse>;

/** POST /api/parking-lot/:itemId/conversation: Talk more with Jabberr. The idea's own conversation (its source when
 *  the caller can open it, else a new one seeded with the note); firstTurn is set when the web must send it. */
export const TalkParkingLotItemResponse = z.object({
  conversationId: z.uuid(),
  projectId: z.uuid(),
  firstTurn: z.object({ clientTurnId: z.string(), content: z.string() }).nullable(),
});
export type TalkParkingLotItemResponse = z.infer<typeof TalkParkingLotItemResponse>;

/** POST /api/parking-lot/:itemId/continue: the idea project's latest conversation the caller can open, or a new one
 *  (firstTurn set) when there is none. */
export const ContinueParkingLotItemResponse = TalkParkingLotItemResponse;
export type ContinueParkingLotItemResponse = TalkParkingLotItemResponse;

/** POST /api/parking-lot/:itemId/comments/:commentId/use: a person applies Jabberr's suggestion as their own edit. */
export const UseSuggestionResponse = z.object({ item: ParkingLotItemDetail, edit: ParkingLotComment });
export type UseSuggestionResponse = z.infer<typeof UseSuggestionResponse>;

/** POST /api/parking-lot/ideas: New idea by talking. A new project idea opens a private idea project (for someone on
 *  the team) with a conversation; an idea for an existing project opens a conversation there. firstTurn is sent by the web. */
export const StartIdeaRequest = z.object({
  title: z.string().trim().max(PARKING_LOT_TITLE_MAX).default(''),
  summary: z.string().trim().max(PARKING_LOT_SUMMARY_MAX).default(''),
  projectId: z.uuid().nullable().optional(),
});
export type StartIdeaRequest = z.infer<typeof StartIdeaRequest>;
export const StartIdeaResponse = z.object({
  projectId: z.uuid(),
  conversationId: z.uuid(),
  /** True when a private idea project was made for it. */
  ideaProject: z.boolean(),
  firstTurn: z.object({ clientTurnId: z.string(), content: z.string() }),
});
export type StartIdeaResponse = z.infer<typeof StartIdeaResponse>;

// --- Preferences (GET and PATCH /api/me/preferences) ---
// One JSON object per person with known keys; unknown keys are refused. Browsers keep a copy only as a cache.
export const Preferences = z
  .object({
    /** Projects pinned to the top of the left nav, in order. */
    pinnedProjects: z.array(z.uuid()).max(50),
    /** Left nav groups the person folded. */
    collapsedNavGroups: z.array(NavGroup).max(4),
    /** The floating Jabberr pet is shown. */
    petVisible: z.boolean(),
    /** How the parking lot colours its notes. */
    parkingColourBy: z.enum(['column', 'project']),
  })
  .partial()
  .strict();
export type Preferences = z.infer<typeof Preferences>;
export const PreferencesResponse = z.object({ preferences: Preferences });
export type PreferencesResponse = z.infer<typeof PreferencesResponse>;
